import { apiGet, apiPost, apiRequest } from './client';

export interface DomainSettings {
  subdomain: string | null;
  customDomain: string | null;
  verificationStatus: 'pending' | 'verifying' | 'verified' | 'failed' | 'connected' | null;
  verificationCode: string | null;
  connectedAt: string | null;
  source: 'byod' | 'purchased' | null;
  projectName: string | null;
}

export interface DomainInstructions {
  cname: {
    type: string;
    name: string;
    target: string;
    description: string;
  };
  /**
   * The apex is a registrar forwarding setting, not a DNS record — registrars
   * refuse a CNAME at a zone root.
   *
   * This replaces an `aRecord` field that used to be here declaring a target of
   * 192.0.2.1, a documentation placeholder that points nowhere. There is no
   * apex IP to publish: Cloudflare provides none for Pages, and a connected
   * zone's apex addresses are produced per-zone by CNAME flattening.
   *
   * `verification` also used to be here. That TXT record now appears only in
   * the Nameserver Delegation panel, where it actually gates zone creation;
   * on this path, pointing `www` at us already proves control.
   */
  apex: {
    type: string;
    from: string;
    to: string;
    description?: string;
  };
  steps: string[];
  projectName: string;
  subdomain: string;
}

export interface DomainVerificationResult {
  cnameVerified: boolean;
  txtVerified: boolean;
  cnameTarget: string | null;
  txtValue: string | null;
  error: string | null;
}

interface GetDomainSettingsResponse {
  success: boolean;
  domain: DomainSettings;
}

interface GetDomainInstructionsResponse {
  success: boolean;
  instructions: DomainInstructions;
}

interface SetDomainResponse {
  success: boolean;
  message: string;
  domain: string;
  verificationStatus: string;
  verificationCode: string;
}

interface VerifyDomainResponse {
  success: boolean;
  message: string;
  domain?: string;
  status?: string;
  verification?: DomainVerificationResult;
  expected?: {
    cname: string;
  };
  error?: string;
}

interface RemoveDomainResponse {
  success: boolean;
  message: string;
}

export async function getDomainSettings(websiteId: string): Promise<DomainSettings> {
  const response = await apiGet<GetDomainSettingsResponse>(
    `/api/websites/${websiteId}/domain`
  );
  return response.domain;
}

export async function getDomainInstructions(websiteId: string): Promise<DomainInstructions> {
  const response = await apiGet<GetDomainInstructionsResponse>(
    `/api/websites/${websiteId}/domain/instructions`
  );
  return response.instructions;
}

export async function setCustomDomain(websiteId: string, domain: string): Promise<SetDomainResponse> {
  return apiPost<SetDomainResponse>(`/api/websites/${websiteId}/domain/set`, { domain });
}

export async function verifyDomain(websiteId: string): Promise<VerifyDomainResponse> {
  return apiPost<VerifyDomainResponse>(`/api/websites/${websiteId}/domain/verify`);
}

export async function removeDomain(websiteId: string): Promise<RemoveDomainResponse> {
  return apiRequest<RemoveDomainResponse>(`/api/websites/${websiteId}/domain`, {
    method: 'DELETE',
  });
}

// =============================================================================
// Domain Purchases (Admin)
// =============================================================================

export interface DomainPurchase {
  id: string;
  domain_name: string;
  registrar: string;
  purchase_status: string;
  ns_propagation_status: string | null;
  purchase_price_cents: number;
  cost_price_cents: number | null;
  markup_cents: number | null;
  expires_at: string | null;
  website_id: string | null;
  created_at: string;
  provisioning_started_at: string | null;
  provisioning_completed_at: string | null;
  stripe_domain_subscription_id: string | null;
}

interface GetDomainPurchasesResponse {
  success: boolean;
  domains: DomainPurchase[];
}

export async function getDomainPurchases(websiteId: string): Promise<DomainPurchase[]> {
  const response = await apiGet<GetDomainPurchasesResponse>(
    `/api/websites/${websiteId}/domain/purchases`
  );
  return response.domains;
}

// =============================================================================
// Nameserver delegation (Admin)
// =============================================================================

export interface DelegationRecord {
  type: string;
  name: string;
  content: string;
  priority?: number;
  via: 'cloudflare-scan' | 'probe';
}

export interface DelegationImportSnapshot {
  imported: DelegationRecord[];
  skipped: DelegationRecord[];
  alreadyPresent: DelegationRecord[];
  failed: Array<{ record: DelegationRecord; error: string }>;
  removed: string[];
}

export interface DelegationRecordRequirement {
  type: 'TXT';
  name: string;
  value: string;
}

export interface DelegationStatus {
  /**
   * Ownership proof. Required only for this path: creating a Cloudflare zone
   * happens before any DNS change, so it is the one step where control cannot
   * be inferred from DNS already pointing at us.
   */
  ownership: {
    verified: boolean;
    record: DelegationRecordRequirement | null;
  };
  domain: string | null;
  zoneId: string | null;
  /** The two nameservers the customer sets at their registrar. Per-zone. */
  nameservers: string[];
  delegationStatus: 'preparing' | 'awaiting-nameservers' | 'active' | 'failed' | null;
  startedAt: string | null;
  importSnapshot: DelegationImportSnapshot | null;
  /** Cloudflare's view of the zone: 'pending' until nameservers are switched. */
  zoneStatus: string | null;
  currentRecords: DelegationRecord[];
}

interface GetDelegationResponse {
  success: boolean;
  delegation: DelegationStatus;
}

interface PrepareDelegationResponse {
  success: boolean;
  message: string;
  delegation?: DelegationStatus & DelegationImportSnapshot;
  warning?: string;
  /** Set when the customer has not yet proven they control the domain. */
  reason?: 'ownership-unverified';
  record?: DelegationRecordRequirement;
}

export async function getDelegation(websiteId: string): Promise<DelegationStatus> {
  const response = await apiGet<GetDelegationResponse>(
    `/api/websites/${websiteId}/domain/delegation`
  );
  return response.delegation;
}

export async function prepareDelegation(websiteId: string): Promise<PrepareDelegationResponse> {
  return apiPost<PrepareDelegationResponse>(
    `/api/websites/${websiteId}/domain/delegation/prepare`
  );
}
